import { Injectable, PLATFORM_ID, inject, signal } from "@angular/core";
import { DOCUMENT, Location, isPlatformBrowser } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { NavigationEnd, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter, firstValueFrom, fromEvent } from "rxjs";
import { environment } from "src/environments/environment";
import { apiBaseUrl } from "src/app/core/helpers/api-base-url";
import {
  FoxyCart,
  FoxyCartItem,
  FoxyCoupon,
} from "src/app/models/foxycart/foxy-cart.model";
import { PriceService } from "src/app/services/price.service";
import { LanguageService } from "src/app/core/services/language.service";

interface CartLinkResponse {
  fields: Record<string, string>;
  extraParams: Record<string, string>;
}

interface FoxyCartGlobal {
  json?: { item_count?: number; session_id?: string };
  client?: { on: (event: string, cb: () => void) => void };
  onLoad?: () => void;
}

declare global {
  interface Window {
    FC?: FoxyCartGlobal;
  }
}

@Injectable({ providedIn: "root" })
export class FoxycartService {
  private http = inject(HttpClient);
  private document = inject(DOCUMENT);
  private priceService = inject(PriceService);
  private languageService = inject(LanguageService);
  private router = inject(Router);
  private location = inject(Location);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly storeDomain = environment.foxyCartStoreDomain;
  readonly cartUrl = `https://${this.storeDomain}/cart`;
  readonly cartItemCount = signal(0);
  readonly cart = signal<FoxyCart | null>(null);

  /** Monotonic counter used to build unique JSONP callback names. */
  private jsonpCounter = 0;

  /**
   * Approach: single shared DGB session (per the DGB handoff,
   * DE/tasks/2026-07-13-jovis-dgb-session-current-endpoint.md). The canonical
   * `.degruyterbrill.com` fcsid jovis adopts so jovis and DGB ride ONE FoxyCart
   * session and cookie-ordering can never resolve a hosted-checkout step to a
   * foreign/empty session. Obtained via a first-party top-level REDIRECT to the
   * DGB endpoint (a cross-site fetch can't read that cookie under Safari ITP),
   * which returns the id in the URL fragment; we persist it first-party and
   * reuse it across reloads. Null when the endpoint is unset/unavailable — we
   * then fall back to the loader's own session, exactly as before.
   */
  private sharedSessionId: string | null = null;
  private static readonly SHARED_SESSION_KEY = "jovis.fcsid";
  /** Guards against a redirect loop when DGB returns without an id (privacy mode). */
  private static readonly REDIRECT_GUARD_KEY = "jovis.fcsid.redirecting";
  /** ISBN of an add-to-cart deferred across the DGB redirect, resumed on return. */
  private static readonly PENDING_ADD_KEY = "jovis.fcsid.pendingAdd";
  /** The id the last DGB round-trip handed us (localStorage, cross-tab). */
  private static readonly SYNCED_KEY = "jovis.fcsid.synced";

  /**
   * Resolves once the FoxyCart loader has initialised the session
   * (`ready.done`), so cart requests carry a real fcsid. Sending an empty
   * fcsid makes FoxyCart spin up a fresh, EMPTY session — which is why a
   * cart read fired before the loader was ready returned no items.
   */
  private resolveLoaderReady!: () => void;
  private loaderReady = new Promise<void>(
    (resolve) => (this.resolveLoaderReady = resolve),
  );

  constructor() {
    if (!this.isBrowser) return;
    this.injectLoader();
    // Fallback: don't block cart reads forever if the loader never signals
    // ready (FoxyCart down, blocked, etc.) — proceed with whatever we have.
    setTimeout(() => this.resolveLoaderReady(), 8000);

    // Approach: single shared DGB session — capture the id if we're returning
    // from the DGB redirect (or reuse the persisted one). Synchronous; the
    // redirect itself is triggered lazily at the two sync points (add-to-cart
    // and the cart page) via ensureSharedSession.
    this.sharedSessionId = this.captureSharedSession();

    // The cart may change in another tab or on the hosted checkout: re-read
    // it when this tab becomes visible again. Never redirects (reconcileSession
    // only replaces a dead id with the store's answer).
    fromEvent(this.document, "visibilitychange")
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.document.visibilityState === "visible") this.refreshCart();
      });
  }

  /**
   * Capture the shared DGB fcsid at bootstrap (synchronous): read it from the
   * `#fcsid=` fragment when we're returning from the DGB redirect (the
   * fragment is stripped later, see `stripFragmentWhenSettled`), else reuse
   * the first-party persisted value. Returns null when the feature is off or we
   * don't have an id yet. Never mints/redirects — that is `ensureSharedSession`.
   */
  private captureSharedSession(): string | null {
    if (!environment.dgbSessionEndpoint) return null;
    const win = this.document.defaultView;
    try {
      const fromHash = new URLSearchParams(
        (win?.location?.hash ?? "").replace(/^#/, ""),
      ).get("fcsid");
      if (
        fromHash &&
        this.getRedirectGuard() &&
        this.isWellFormedId(fromHash)
      ) {
        this.writePersistedSession(fromHash);
        this.markSynced(fromHash);
        this.clearRedirectGuard();
        this.stripFragmentWhenSettled();
        return fromHash;
      }
      if (fromHash) {
        // Either nobody asked DGB for this id (a bookmarked or shared URL, or
        // a crafted link — session fixation) or it isn't a store id at all.
        // Ignore it and remove it before the loader can pick it up; keep
        // whatever we already hold.
        this.stripFragmentWhenSettled({ waitForLoader: false });
      }
    } catch {
      // URLSearchParams can throw on a malformed hash — nothing to adopt then
    }
    return this.readPersistedSession();
  }

  /** The store's own session-id shape (the loader accepts `[A-Za-z0-9]*`). */
  private isWellFormedId(id: string): boolean {
    return /^[A-Za-z0-9]{8,64}$/.test(id);
  }

  /**
   * Drop `#fcsid=` from the URL so the bearer id doesn't linger in the address
   * bar, history, or a bookmark — but only once (a) the Router's initial
   * navigation has ended, because the Router then writes the URL it navigated
   * to, fragment included, and would undo an earlier strip; and (b) the
   * FoxyCart loader is ready, because the loader reads `#fcsid=` itself and
   * that is what keeps its own `.jovis.de` session cookie on the shared id.
   * Goes through Angular's Location so the strategy/base href stay consistent.
   */
  private stripFragmentWhenSettled({ waitForLoader = true } = {}): void {
    const win = this.document.defaultView;
    if (!waitForLoader) {
      // Unsolicited id: take it out right now (before the loader script
      // runs); the router's own URL write is caught by the deferred strip.
      try {
        win?.history.replaceState(
          null,
          "",
          `${win.location.pathname}${win.location.search}`,
        );
      } catch {
        /* URL hygiene only — never fatal */
      }
    }
    const navigated = this.router.navigated
      ? Promise.resolve()
      : firstValueFrom(
          this.router.events.pipe(filter((e) => e instanceof NavigationEnd)),
        );
    Promise.all([navigated, waitForLoader ? this.loaderReady : undefined])
      .then(() => {
        const hash = win?.location?.hash ?? "";
        if (!/(^#|&)fcsid=/.test(hash)) return;
        // Keep the Router's history state (navigationId) so scroll
        // restoration on Back still works for this entry.
        this.location.replaceState(
          this.location.path(false),
          "",
          this.location.getState(),
        );
      })
      .catch(() => {
        /* URL hygiene only — never fatal */
      });
  }

  /**
   * Ensure jovis holds a shared DGB fcsid worth adding to, before a cart
   * action. This is the ONLY place that TOP-LEVEL NAVIGATES to the DGB
   * endpoint (which reads/mints the `.degruyterbrill.com` session and
   * redirects back with `#fcsid=`); a cross-site fetch can't read that cookie
   * under ITP, so a redirect is the only reliable mechanism. Callers are the
   * two sync points — add-to-cart and the cart page — never the homepage or a
   * product page, because the bounce is shopper-visible.
   *
   * Redirects when:
   * - no id is held (first visit, or storage cleared), or
   * - an id is held but the cart is EMPTY and this browser session has not
   *   been through a DGB round-trip yet. A dead id (session removed after a
   *   completed checkout, expired, rotated by DGB) always reads as an empty
   *   cart, so this re-sync heals it; a live-but-empty cart costs one bounce
   *   per browser session. A non-empty cart is never re-synced: it lives in
   *   the held session and moving to DGB's would orphan it.
   *
   * Never redirects when the endpoint is unset (inert), after a return that
   * carried no id (privacy mode — the loop guard), or
   * once this browser session is synced: a dead id then degrades to the
   * session the store echoes (see `reconcileSession`) instead of bouncing.
   *
   * Returns true when the caller proceeds, false when we've navigated away
   * (the app re-bootstraps on return).
   */
  async ensureSharedSession(returnUrl?: string): Promise<boolean> {
    if (!this.isBrowser) return true;
    const endpoint = environment.dgbSessionEndpoint;
    if (!endpoint) return true;
    const win = this.document.defaultView;
    if (!win) return true;

    this.sharedSessionId = this.readPersistedSession() ?? this.sharedSessionId;
    if (!(await this.shouldResync())) return true;

    // Came back from a redirect still without an id (privacy mode) — don't loop.
    if (this.getRedirectGuard()) return true;

    // Whatever we hold stays until DGB answers: a return with #fcsid replaces
    // it, a return without one leaves the shopper on it.
    this.setRedirectGuard();
    const callback = (returnUrl ?? win.location.href).split("#")[0];
    this.redirectTo(
      `${endpoint}?callback=${encodeURIComponent(callback)}` +
        `&rotate=false&returnId=true`,
    );
    return false; // navigating away to fetch the shared session
  }

  /**
   * Whether the session we are on should be traded for a DGB round-trip:
   * only while the cart is empty, and never twice for the same id. Reads the
   * cart through the id we would keep (the held one, else the loader's) when
   * it hasn't been loaded yet, so the emptiness check never trusts a badge
   * seeded from another session — that read also lets `reconcileSession`
   * replace a dead id beforehand. An unreadable cart is not an empty cart:
   * no bounce then. With no session at all there is nothing to read.
   */
  private async shouldResync(): Promise<boolean> {
    if (this.sharedSessionId && this.isSynced()) return false;
    if (!this.cart() && this.preferredSessionId()) {
      try {
        await this.loadCart();
      } catch {
        return false;
      }
    }
    return this.cartItemCount() === 0;
  }

  /** Overridable navigation seam (unit tests must not leave the runner). */
  private redirectTo(url: string): void {
    this.document.defaultView?.location.assign(url);
  }

  /**
   * Whether the held id is the one the last DGB round-trip handed us. The
   * marker is keyed by id and lives in localStorage, so it holds across tabs
   * and reloads and expires by itself when the id is replaced. Fails CLOSED
   * like the redirect guard: unreadable storage → no re-sync.
   */
  private isSynced(): boolean {
    try {
      return (
        this.document.defaultView?.localStorage?.getItem(
          FoxycartService.SYNCED_KEY,
        ) === this.sharedSessionId
      );
    } catch {
      return true;
    }
  }

  private markSynced(id: string): void {
    try {
      this.document.defaultView?.localStorage?.setItem(
        FoxycartService.SYNCED_KEY,
        id,
      );
    } catch {
      /* storage blocked — non-fatal */
    }
  }

  /**
   * The fcsid every store request is pinned to: the shared DGB session when
   * available, else the loader's own session (prior behaviour).
   */
  private preferredSessionId(): string {
    // Another tab may have adopted a newer id since we booted: localStorage
    // is the source of truth while the shared-session feature is on.
    if (environment.dgbSessionEndpoint) {
      this.sharedSessionId =
        this.readPersistedSession() ?? this.sharedSessionId;
    }
    return this.sharedSessionId ?? window.FC?.json?.session_id ?? "";
  }

  /**
   * Whether a DGB round-trip is in flight or came back without an id. Fails
   * CLOSED: if storage is blocked we cannot remember that we already
   * redirected, so we must behave as if we had — never bounce, and accept the
   * id a return carries.
   */
  private getRedirectGuard(): boolean {
    try {
      return !!this.document.defaultView?.sessionStorage?.getItem(
        FoxycartService.REDIRECT_GUARD_KEY,
      );
    } catch {
      return true;
    }
  }

  private setRedirectGuard(): void {
    try {
      this.document.defaultView?.sessionStorage?.setItem(
        FoxycartService.REDIRECT_GUARD_KEY,
        "1",
      );
    } catch {
      /* storage blocked — non-fatal */
    }
  }

  private clearRedirectGuard(): void {
    try {
      this.document.defaultView?.sessionStorage?.removeItem(
        FoxycartService.REDIRECT_GUARD_KEY,
      );
    } catch {
      /* storage blocked — non-fatal */
    }
  }

  /**
   * Remember an add-to-cart the shopper triggered so it can be completed after
   * the DGB session redirect returns (auto-resume). Keyed by ISBN.
   */
  rememberPendingAdd(isbn: string): void {
    try {
      this.document.defaultView?.sessionStorage?.setItem(
        FoxycartService.PENDING_ADD_KEY,
        JSON.stringify({ isbn, at: Date.now() }),
      );
    } catch {
      /* storage blocked — non-fatal */
    }
  }

  /** A deferred add is only honoured this long after it was remembered. */
  private static readonly PENDING_ADD_TTL_MS = 10 * 60 * 1000;

  /**
   * The ISBN of a deferred add-to-cart (without consuming it), or null. A
   * marker older than the TTL is dropped so a stranded round-trip can't fire
   * an unasked add when the shopper comes back to the page much later.
   */
  peekPendingAdd(): string | null {
    try {
      const raw = this.document.defaultView?.sessionStorage?.getItem(
        FoxycartService.PENDING_ADD_KEY,
      );
      if (!raw) return null;
      if (!raw.startsWith("{")) return raw; // marker written by an older build
      const { isbn, at } = JSON.parse(raw) as { isbn?: string; at?: number };
      if (
        typeof at === "number" &&
        Date.now() - at > FoxycartService.PENDING_ADD_TTL_MS
      ) {
        this.clearPendingAdd();
        return null;
      }
      return isbn ?? null;
    } catch {
      return null;
    }
  }

  clearPendingAdd(): void {
    try {
      this.document.defaultView?.sessionStorage?.removeItem(
        FoxycartService.PENDING_ADD_KEY,
      );
    } catch {
      /* storage blocked — non-fatal */
    }
  }

  private readPersistedSession(): string | null {
    try {
      return (
        this.document.defaultView?.localStorage?.getItem(
          FoxycartService.SHARED_SESSION_KEY,
        ) || null
      );
    } catch {
      return null; // storage unavailable/blocked — non-fatal
    }
  }

  private writePersistedSession(id: string): void {
    try {
      this.document.defaultView?.localStorage?.setItem(
        FoxycartService.SHARED_SESSION_KEY,
        id,
      );
    } catch {
      // storage blocked (e.g. Safari private mode) — non-fatal
    }
  }

  /** Inject FoxyCart loader.js script into the page */
  private injectLoader() {
    // The loader establishes FoxyCart's own session for this site (cookie at
    // `.jovis.de`) and fires `ready.done` once the store answered; cart reads
    // and mutations themselves go through our JSONP requests (fcRequest).
    window.FC = window.FC || {};
    window.FC.onLoad = () => {
      window.FC?.client?.on("ready.done", () => {
        const loaderSession = window.FC?.json?.session_id;
        if (
          this.sharedSessionId &&
          loaderSession &&
          loaderSession !== this.sharedSessionId
        ) {
          // The loader booted a session of its own; the badge must reflect
          // the shared one, so read the cart through our id instead.
          this.refreshCart();
        } else {
          this.cartItemCount.set(window.FC?.json?.item_count ?? 0);
        }
        // Session is now established (window.FC.json.session_id is set).
        this.resolveLoaderReady();
      });
    };

    const script = this.document.createElement("script");
    script.src = `https://cdn.foxycart.com/${environment.foxyCartSubdomain}/loader.js`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-cfasync", "false");
    // A blocked or failing CDN must not hold requests for the 8 s fallback.
    script.onerror = () => this.resolveLoaderReady();
    this.document.head.appendChild(script);
  }

  /**
   * Refresh the cart from the FoxyCart session (e.g. when the tab regains
   * focus after the user changed the cart elsewhere). Routes through the same
   * `loadCart`/`fcRequest` path as every other read so there is a single
   * source of truth; failures are non-fatal for a background refresh.
   */
  private refreshCart() {
    this.loadCart().catch(() => {
      /* background refresh — keep the last known cart on failure */
    });
  }

  // Search for a cookie by name. Returns undefined if not found or on the server.
  private getCookie(name: string): string | undefined {
    if (!this.isBrowser) return undefined;
    const match = this.document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    return match
      ? decodeURIComponent(match.split("=").slice(1).join("="))
      : undefined;
  }

  /**
   * Get a cart link for a product by ISBN.
   * The backend fetches the product from DB, builds fields, and signs them.
   * This prevents price tampering from the frontend.
   */
  async getCartLink(
    isbn: string,
    currency: string,
    language: string,
  ): Promise<CartLinkResponse> {
    return firstValueFrom(
      this.http.get<CartLinkResponse>(
        `${apiBaseUrl()}/foxycart/cart-link/${isbn}`,
        { params: { currency, language } },
      ),
    );
  }

  /**
   * Map the raw FoxyCart JSON cart into our typed model.
   * Numeric fields arrive as strings, so they are parsed. The isbn is derived
   * best-effort from the item `code` (e.g. "lang=en|subject=AD|10.1234/<isbn>|store=jovis").
   */
  private mapCart(raw: any): FoxyCart {
    const rawItems: any[] = Array.isArray(raw?.items) ? raw.items : [];
    const items: FoxyCartItem[] = rawItems.map((item) => {
      const options: any[] = Array.isArray(item?.options) ? item.options : [];
      const optionValue = (className: string): string | undefined => {
        const option = options.find((opt) => opt?.class === className);
        return option?.value != null ? String(option.value) : undefined;
      };

      return {
        id: String(item?.id ?? ""),
        name: String(item?.name ?? ""),
        price: this.toNumber(item?.price),
        quantity: this.toNumber(item?.quantity),
        quantityMax:
          item?.quantity_max != null
            ? this.toNumber(item.quantity_max)
            : undefined,
        image: item?.image,
        code: item?.code,
        isbn: optionValue("isbn") ?? this.deriveIsbn(item?.code),
        format: optionValue("format"),
        language: this.deriveLanguage(item?.code),
        category: item?.category != null ? String(item.category) : undefined,
      };
    });

    // NOTE: the exact applied-coupon JSON shape (array vs object keyed by code,
    // and which field carries the id) is unverified pending a real test coupon,
    // so this normalization is best-effort. FoxyCart has been seen returning
    // either an array or an object keyed by coupon code.
    const rawCoupons: any[] = Array.isArray(raw?.coupons)
      ? raw.coupons
      : raw?.coupons && typeof raw.coupons === "object"
        ? Object.values(raw.coupons)
        : [];
    const coupons: FoxyCoupon[] = rawCoupons.map((coupon) => {
      const rawId = coupon?.id ?? coupon?.coupon_code_id;
      return {
        id: rawId != null ? String(rawId) : undefined,
        name: String(coupon?.name ?? ""),
        amount: this.toNumber(coupon?.amount),
        code: coupon?.code,
      };
    });

    return {
      items,
      itemCount: this.toNumber(raw?.item_count),
      totalItemPrice: this.toNumber(raw?.total_item_price),
      totalDiscount: this.toNumber(raw?.total_discount),
      totalOrder: this.toNumber(raw?.total_order),
      coupons,
      messages: {
        errors: Array.isArray(raw?.messages?.errors) ? raw.messages.errors : [],
        warnings: Array.isArray(raw?.messages?.warnings)
          ? raw.messages.warnings
          : [],
        info: Array.isArray(raw?.messages?.info) ? raw.messages.info : [],
      },
      sessionId: String(raw?.session_id ?? ""),
      paymentInfoRequired: !!raw?.payment_info_required,
    };
  }

  private toNumber(value: unknown): number {
    const parsed = parseFloat(String(value));
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /** Best-effort isbn extraction from a FoxyCart item code. */
  private deriveIsbn(code?: string): string | undefined {
    if (!code) return undefined;
    const doiMatch = code.match(/10\.\d+\/(\d{10,13}[\dXx]?)/);
    if (doiMatch) return doiMatch[1];
    const pipeMatch = code.match(/\|(\d{10,13})\|/);
    return pipeMatch ? pipeMatch[1] : undefined;
  }

  /** Best-effort language extraction from a FoxyCart item code (`lang=xx`). */
  private deriveLanguage(code?: string): string | undefined {
    if (!code) return undefined;
    const match = code.match(/lang=(\w+)/);
    return match ? match[1] : undefined;
  }

  /** Store requests run one at a time (see `fcRequest`). */
  private queue: Promise<unknown> = Promise.resolve();

  /**
   * Issue a JSONP request to the FoxyCart session cart endpoint. Every
   * request carries an explicit fcsid; a known id is honoured, an unknown or
   * empty one makes the store mint a fresh session (which `reconcileSession`
   * then adopts). Requests are serialised so that a dead id is reconciled
   * exactly once: the next request reads the session id only after the
   * previous answer is in, instead of two requests minting two sessions.
   * SSR-safe: rejects on the server (no DOM / no window.FC).
   */
  private fcRequest(query: string): Promise<any> {
    if (!this.isBrowser) {
      return Promise.reject(new Error("FoxyCart requests require a browser"));
    }
    const run = this.queue.then(() => this.send(query));
    this.queue = run.catch(() => {
      /* a failed request must not block the next one */
    });
    return run;
  }

  private async send(query: string): Promise<any> {
    // Without a session id of our own, wait for the loader to establish one
    // so we send a real fcsid (an empty one makes FoxyCart mint a fresh,
    // empty session). With the shared id held there is nothing to wait for —
    // a blocked or slow loader must not stall the cart.
    if (!this.preferredSessionId()) await this.loaderReady;

    return new Promise((resolve, reject) => {
      const callbackName = `__fcJsonp${this.jsonpCounter++}`;
      const sessionId = this.preferredSessionId();
      const script = this.document.createElement("script");
      const global = window as unknown as Record<string, unknown>;
      let timer: ReturnType<typeof setTimeout>;

      const cleanup = () => {
        clearTimeout(timer);
        delete global[callbackName];
        script.remove();
      };

      global[callbackName] = (response: any) => {
        cleanup();
        this.reconcileSession(sessionId, response?.session_id);
        resolve(response);
      };

      script.onerror = () => {
        cleanup();
        reject(new Error("FoxyCart JSONP request failed"));
      };

      // Guard against a response that never invokes the callback (HTTP 200 with
      // a body that drops it): otherwise the Promise hangs and the script leaks.
      timer = setTimeout(() => {
        script.remove();
        // A late answer must not throw at a deleted global — leave a no-op.
        global[callbackName] = () => {};
        reject(new Error("FoxyCart JSONP request timed out"));
      }, 15000);

      // FoxyCart serves JSONP via `output=json&callback=` (it returns
      // `/**/cb({...})`). NOTE: `output=json-p` is NOT recognized — the store
      // returns its HTML cart for that, so the callback never fires.
      script.src =
        `${this.cartUrl}?${query}` +
        `&fcsid=${encodeURIComponent(sessionId)}` +
        `&output=json&callback=${callbackName}`;
      this.document.body.appendChild(script);
    });
  }

  /**
   * The store answers every request with the session it actually used. When
   * that differs from the id we sent, ours is dead (removed after a completed
   * checkout, expired, or rotated on the DGB side) and the store has minted a
   * fresh one — adopt it. Otherwise every further request would land in yet
   * another throwaway session and the cart would read empty forever ("nothing
   * can be added"). Also catches an EMPTY sent id (the 8 s loader fallback
   * fired first), which the store likewise answers with a fresh session.
   * Persisted only while the shared-session feature is on (the persisted slot
   * is its first-party copy).
   */
  private reconcileSession(sent: string, echoed: unknown): void {
    if (typeof echoed !== "string" || !echoed || echoed === sent) return;
    // With the feature off an empty id only means the loader isn't ready;
    // its own cookie session will show up — don't pin a throwaway one.
    if (!sent && !environment.dgbSessionEndpoint) return;
    // Only the id we still hold may be replaced; a late answer for an id we
    // already moved away from is stale.
    if (sent !== this.preferredSessionId()) return;
    this.sharedSessionId = echoed;
    if (environment.dgbSessionEndpoint) this.writePersistedSession(echoed);
  }

  /**
   * Compute the FoxyCart `template_set` for the app's current currency and
   * language, in the same `LANG_CURRENCY` form the backend builds (e.g.
   * `EN_EUR`). Carrying this on reads/updates keeps the session localized so
   * prices don't drift to a default currency.
   */
  private templateSet(): string {
    const lang = this.languageService.currentLanguage() ?? "";
    const currency = this.priceService.currency ?? "";
    return `${lang.toUpperCase()}_${currency}`;
  }

  /** Fetch the current cart from the FoxyCart session. */
  async loadCart(): Promise<FoxyCart> {
    const raw = await this.fcRequest(
      `cart=view&template_set=${encodeURIComponent(this.templateSet())}`,
    );
    const cart = this.mapCart(raw);
    this.cart.set(cart);
    this.cartItemCount.set(cart.itemCount);
    return cart;
  }

  /**
   * Re-localize the cart for the app's current currency/language. Used by the
   * currency and language switchers so cart totals re-render in the new locale.
   */
  async reloadForLocale(): Promise<void> {
    // Nothing to re-localize before a session exists (a read now would only
    // mint a throwaway session).
    if (!this.cart() && !this.preferredSessionId()) return;
    await this.loadCart();
  }

  /**
   * Add a product to the cart using the backend-signed fields + extraParams.
   * Cart validation is on, so only the signed fields and extraParams are sent.
   */
  async addItem(
    fields: Record<string, string>,
    extraParams?: Record<string, string>,
  ): Promise<FoxyCart> {
    const params = new URLSearchParams();

    for (const [name, value] of Object.entries(fields)) {
      if (value !== undefined) params.append(name, value);
    }
    if (extraParams) {
      for (const [name, value] of Object.entries(extraParams)) {
        params.append(name, value);
      }
    }

    await this.fcRequest(params.toString());
    const cart = await this.loadCart();

    // Electronic products (e-books) are capped at 1 per cart. FoxyCart's cart
    // API does NOT enforce this (downloadable_id is 0, so re-adding bumps the
    // quantity freely) — only its checkout template corrects it client-side.
    // We normalize on-site: if a re-add pushed an electronic line past 1, clamp
    // it back to 1 here.
    const overQuantityElectronic = cart.items.find(
      (item) => item.category === "electronic" && item.quantity > 1,
    );
    if (overQuantityElectronic) {
      return this.setQuantity(overQuantityElectronic.id, 1);
    }

    return cart;
  }

  /** Set the quantity of a cart line by item id. */
  async setQuantity(itemId: string, qty: number): Promise<FoxyCart> {
    await this.fcRequest(
      `cart=update&quantity=${qty}&id=${itemId}` +
        `&template_set=${encodeURIComponent(this.templateSet())}`,
    );
    return this.loadCart();
  }

  /** Remove a cart line by item id (quantity 0). */
  async removeItem(itemId: string): Promise<FoxyCart> {
    return this.setQuantity(itemId, 0);
  }

  /**
   * Hand the shopper off to FoxyCart's prefilled hosted checkout
   * (`cart=checkout`), which collects the address and processes payment via
   * Worldline — card data never touches jovis.de. No-op on the server or when
   * the (re-read) cart is empty. Carries the current `template_set` so the
   * checkout renders in the user's currency/language.
   */
  async goToCheckout(): Promise<void> {
    if (!this.isBrowser) return;
    // Re-read through the held id right before the hand-off: a session that
    // died while the tab sat idle is replaced by the store's answer here
    // (reconcileSession) rather than handed off to an empty hosted checkout.
    const cart = await this.loadCart().catch(() => this.cart());
    if (!cart?.itemCount) return;

    const sessionId = this.preferredSessionId();
    const url =
      `${this.cartUrl}?cart=checkout` +
      `&fcsid=${encodeURIComponent(sessionId)}` +
      `&template_set=${encodeURIComponent(this.templateSet())}` +
      `&h:consent=${this.getCookie("acceptcookies") === "true" ? "granted" : "denied"}`;
    this.navigateTo(url);
  }

  /**
   * Overridable navigation seam so `goToCheckout` is unit-testable without the
   * browser actually navigating away.
   */
  private navigateTo(url: string) {
    this.document.defaultView?.location.assign(url);
  }

  /**
   * Apply a coupon code. Returns `{ ok: false, error }` when FoxyCart reports a
   * coupon-context error, otherwise `{ ok: true }`.
   */
  async applyCoupon(code: string): Promise<{ ok: boolean; error?: string }> {
    const raw = await this.fcRequest(
      `coupon=${encodeURIComponent(code)}` +
        `&template_set=${encodeURIComponent(this.templateSet())}`,
    );
    const cart = this.mapCart(raw);
    this.cart.set(cart);
    this.cartItemCount.set(cart.itemCount);

    const couponError = cart.messages.errors.find(
      (error) => error.context === "coupon",
    );
    if (couponError) {
      return { ok: false, error: couponError.message };
    }
    return { ok: true };
  }

  /**
   * Remove an applied coupon. FoxyCart keys removal by the coupon's numeric
   * id (`coupon_code_id`), not its code.
   */
  async removeCoupon(couponId: string): Promise<FoxyCart> {
    await this.fcRequest(
      `cart=remove_coupon&coupon_code_id=${encodeURIComponent(couponId)}` +
        `&template_set=${encodeURIComponent(this.templateSet())}`,
    );
    return this.loadCart();
  }
}
